'use client'

import React, { useState } from 'react'
import Link from 'next/link'

type NavLink = {
  href?: string
  label: string
  plain?: boolean // render as <a> (e.g. /admin)
  cta?: boolean // audience switch — rendered as a distinct pill
  children?: NavLink[] // dropdown group (parent label has no href)
  icon?: React.ReactNode // shown in dropdown rows
  desc?: string // small description in dropdown rows
  tourId?: string // exposes the item as a coachmark-tour target (data-tour)
}

function NavItem({ link, onNavigate }: { link: NavLink; onNavigate: () => void }) {
  const [open, setOpen] = useState(false)

  if (link.children) {
    return (
      <div className={`nav-group${open ? ' open' : ''}`}>
        <button
          className="nav-group__label"
          aria-haspopup="true"
          aria-expanded={open}
          data-tour={link.tourId}
          onClick={() => setOpen((o) => !o)}
        >
          {link.label}
          <span className="nav-group__caret" aria-hidden="true">
            ▾
          </span>
        </button>
        <div className="nav-dropdown">
          {link.children.map((c) => (
            <Link
              key={c.href}
              className="nav-dd-item"
              href={c.href || '#'}
              onClick={() => {
                setOpen(false)
                onNavigate()
              }}
            >
              {c.icon ? <span className="nav-dd-icon">{c.icon}</span> : null}
              <span className="nav-dd-text">
                <span className="nav-dd-label">{c.label}</span>
                {c.desc ? <span className="nav-dd-desc">{c.desc}</span> : null}
              </span>
            </Link>
          ))}
        </div>
      </div>
    )
  }

  const cls = link.cta ? 'nav-cta' : undefined
  return link.plain ? (
    <a className={cls} href={link.href} data-tour={link.tourId} onClick={onNavigate}>
      {link.label}
    </a>
  ) : (
    <Link className={cls} href={link.href || '#'} data-tour={link.tourId} onClick={onNavigate}>
      {link.label}
    </Link>
  )
}

// Shared responsive nav with a hamburger on mobile. Supports dropdown groups (children) and a CTA
// pill (cta) for the audience switch.
export function SiteNav({
  brand,
  brandHref = '/',
  links,
  powered = false,
}: {
  brand: React.ReactNode
  brandHref?: string
  links: NavLink[]
  powered?: boolean
}) {
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)

  return (
    <nav className="nav">
      <div className="nav-inner">
        <Link href={brandHref} className="nav-brand" onClick={close} style={{ textDecoration: 'none' }}>
          {brand}
        </Link>
        <button
          className="nav-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className={`nav-links${open ? ' open' : ''}`}>
          {links.map((l) => (
            <NavItem key={l.href || l.label} link={l} onNavigate={close} />
          ))}
          {powered ? (
            <span className="powered">
              Powered by <strong>B.C. Strata Tech</strong>
            </span>
          ) : null}
        </div>
      </div>
    </nav>
  )
}
